/**
 * Seedable PRNG — xoshiro256** algorithm
 * Reference: https://prng.di.unimi.it/xoshiro256starstar.c
 *
 * Provides deterministic random number generation from a string seed.
 * All BigInt arithmetic is masked to 64 bits.
 */

const MASK = 0xFFFF_FFFF_FFFF_FFFFn;

export interface PRNG {
  /** Returns a float in [0, 1) with 53-bit mantissa precision */
  next(): number;
  /** Returns an integer in [min, max] inclusive */
  nextInt(min: number, max: number): number;
  /** Returns a random element from the array */
  pick<T>(array: readonly T[]): T;
  /** Returns a new shuffled copy of the array (Fisher-Yates) */
  shuffle<T>(array: readonly T[]): T[];
  /** Creates an independent copy of this PRNG with the same state */
  fork(): PRNG;
  /**
   * Return a copy of the 4-lane xoshiro256** internal state. Used by debug
   * replay tooling (--dump-rng) to diff the PRNG across runs. Returning a
   * copy keeps callers from mutating the live state.
   */
  snapshot(): [bigint, bigint, bigint, bigint];
}

/** SplitMix64 hash — used to expand a single seed into the 4x uint64 state */
export function splitmix64(seed: bigint): bigint {
  let z = (seed + 0x9E3779B97F4A7C15n) & MASK;
  z = ((z ^ (z >> 30n)) * 0xBF58476D1CE4E5B9n) & MASK;
  z = ((z ^ (z >> 27n)) * 0x94D049BB133111EBn) & MASK;
  return (z ^ (z >> 31n)) & MASK;
}

function rotl(x: bigint, k: bigint): bigint {
  return ((x << k) | (x >> (64n - k))) & MASK;
}

function stringToSeed(seedStr: string): bigint {
  let h = 0n;
  for (let i = 0; i < seedStr.length; i++) {
    h = ((h * 31n) + BigInt(seedStr.charCodeAt(i))) & MASK;
  }
  return h;
}

/**
 * Create a seeded PRNG from a string seed.
 * Uses SplitMix64 to expand the seed into the 4-element xoshiro256** state.
 */
export function createRNG(seedStr: string): PRNG {
  let sm = stringToSeed(seedStr);

  const s: [bigint, bigint, bigint, bigint] = [0n, 0n, 0n, 0n];
  for (let i = 0; i < 4; i++) {
    sm = (sm + 0x9E3779B97F4A7C15n) & MASK;
    s[i] = splitmix64(sm);
  }

  return makePRNG(s);
}

function makePRNG(s: [bigint, bigint, bigint, bigint]): PRNG {
  function nextRaw(): bigint {
    const result = (rotl((s[1] * 5n) & MASK, 7n) * 9n) & MASK;
    const t = (s[1] << 17n) & MASK;

    s[2] ^= s[0];
    s[3] ^= s[1];
    s[1] ^= s[2];
    s[0] ^= s[3];

    s[2] ^= t;
    s[3] = rotl(s[3], 45n);

    return result;
  }

  function next(): number {
    return Number(nextRaw() >> 11n) / (2 ** 53);
  }

  function nextInt(min: number, max: number): number {
    return min + Math.floor(next() * (max - min + 1));
  }

  function pick<T>(array: readonly T[]): T {
    if (array.length === 0) {
      throw new Error('Cannot pick from empty array');
    }
    return array[nextInt(0, array.length - 1)]!;
  }

  function shuffle<T>(array: readonly T[]): T[] {
    const result = [...array];
    for (let i = result.length - 1; i > 0; i--) {
      const j = nextInt(0, i);
      const temp = result[i]!;
      result[i] = result[j]!;
      result[j] = temp;
    }
    return result;
  }

  function fork(): PRNG {
    return makePRNG([...s] as [bigint, bigint, bigint, bigint]);
  }

  function snapshot(): [bigint, bigint, bigint, bigint] {
    return [s[0], s[1], s[2], s[3]];
  }

  return { next, nextInt, pick, shuffle, fork, snapshot };
}
